import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import usersService from './usersService';

// Tip tanımlamaları
export interface Role {
  id: number;
  key: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface UserCompany {
  company_id: number;
  company_name?: string;
  role?: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  status: string;
  roles: string[]; // Role[] yerine string[] olarak değiştirildi
  companies: UserCompany[];
  created_at: string;
  updated_at: string;
}

export interface UserCreate {
  username: string;
  email: string;
  full_name: string;
  password: string;
  status: string;
  role_ids: number[];
  company_ids: number[];
}

export interface UserUpdate {
  username?: string;
  email?: string;
  full_name?: string;
  password?: string;
  status?: string;
  role_ids?: number[];
  company_ids?: number[];
}

export interface UsersState {
  users: User[];
  selectedUser: User | null;
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  limit: number;
  filters: {
    status?: string;
    role_id?: number;
    company_id?: number;
    search?: string;
  };
  viewMode: 'list' | 'grid';
}

// Hata mesajını çıkarma yardımcı fonksiyonu
const extractErrorMessage = (error: any): string => {
  if (typeof error === 'string') return error;
  
  if (Array.isArray(error)) {
    return error.map(err => err.msg || JSON.stringify(err)).join(', ');
  }
  
  if (error?.detail) {
    if (typeof error.detail === 'string') {
      return error.detail;
    }
    if (Array.isArray(error.detail)) {
      return error.detail.map((item: any) => 
        item.msg || JSON.stringify(item)
      ).join(', ');
    }
  }
  
  if (error?.message) return error.message;
  
  if (error?.msg) return error.msg;
  
  try {
    return JSON.stringify(error) || 'An unexpected error occurred';
  } catch (e) {
    return 'An unexpected error occurred';
  }
};

// Kullanıcıları getir
export const getUsers = createAsyncThunk(
  'users/getUsers',
  async (params: { 
    page?: number; 
    limit?: number; 
    status?: string; 
    role_id?: number; 
    company_id?: number;
    search?: string;
  }, { rejectWithValue }) => {
    try {
      return await usersService.getUsers(params);
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Get users error:', axiosError.response?.data);
      return rejectWithValue(
        axiosError.response?.data || { message: 'Failed to fetch users.' }
      );
    }
  }
);

// Kullanıcı detaylarını getir
export const getUserById = createAsyncThunk(
  'users/getUserById',
  async (userId: number, { rejectWithValue }) => {
    try {
      return await usersService.getUserById(userId);
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Get user error:', axiosError.response?.data);
      return rejectWithValue(
        axiosError.response?.data || { message: 'Failed to fetch user details.' }
      );
    }
  }
);

// Yeni kullanıcı oluştur
export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData: UserCreate, { rejectWithValue }) => {
    try {
      return await usersService.createUser(userData);
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Create user error:', axiosError.response?.data);
      return rejectWithValue(
        axiosError.response?.data || { message: 'Failed to create user.' }
      );
    }
  }
);

// Kullanıcı güncelle
export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ userId, userData }: { userId: number; userData: UserUpdate }, { rejectWithValue }) => {
    try {
      return await usersService.updateUser(userId, userData);
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Update user error:', axiosError.response?.data);
      return rejectWithValue(
        axiosError.response?.data || { message: 'Failed to update user.' }
      );
    }
  }
);

// Kullanıcı sil
export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (userId: number, { rejectWithValue }) => {
    try {
      await usersService.deleteUser(userId);
      return userId;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Delete user error:', axiosError.response?.data);
      return rejectWithValue(
        axiosError.response?.data || { message: 'Failed to delete user.' }
      );
    }
  }
);

// Başlangıç durumu
const initialState: UsersState = {
  users: [],
  selectedUser: null,
  isLoading: false,
  error: null,
  totalCount: 0,
  page: 1,
  limit: 10,
  filters: {},
  viewMode: 'list',
};

// Users slice
const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
    },
    setFilters: (state, action: PayloadAction<UsersState['filters']>) => {
      state.filters = action.payload;
      state.page = 1; // Reset to first page when filters change
    },
    setViewMode: (state, action: PayloadAction<'list' | 'grid'>) => {
      state.viewMode = action.payload;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Users
      .addCase(getUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload.users;
        state.totalCount = action.payload.total;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = extractErrorMessage(action.payload);
      })
      
      // Get User by ID
      .addCase(getUserById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedUser = action.payload;
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = extractErrorMessage(action.payload);
      })
      
      // Create User
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = extractErrorMessage(action.payload);
      })
      
      // Update User
      .addCase(updateUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.selectedUser && state.selectedUser.id === action.payload.id) {
          state.selectedUser = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = extractErrorMessage(action.payload);
      })
      
      // Delete User
      .addCase(deleteUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = state.users.filter(user => user.id !== action.payload);
        state.totalCount -= 1;
        if (state.selectedUser && state.selectedUser.id === action.payload) {
          state.selectedUser = null;
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = extractErrorMessage(action.payload);
      });
  },
});

export const { 
  clearError, 
  setPage, 
  setLimit, 
  setFilters, 
  setViewMode,
  clearSelectedUser 
} = usersSlice.actions;
export default usersSlice.reducer;